import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
} from '@angular/core';

@Component({
  selector: 'chart-legend',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<div class="html-legend-container" [id]="containerID()"></div>',
  styles: [
    `
      .html-legend-container .divContainer {
        display: flex;
        flex-direction: row;
      }

      .html-legend-container .divContainer ul {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
      }
      .html-legend-container .divContainer ul li {
        display: flex;
        align-items: center;
        cursor: pointer;
        margin-left: 8px;
        margin-bottom: 8px;
      }
      .html-legend-container .divContainer ul li span.legend-circle {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px 8px;
        font-size: 12px;
        border: 1px solid;
        border-radius: 999px;
        text-align: center;
        white-space: nowrap;
      }
    `,
  ],
})
export class ChartLegendComponent {
  private renderer: Renderer2 = inject(Renderer2);
  private el: ElementRef = inject(ElementRef);
  containerID = input<string>();
  label = input<string | null>();
  ready = output<ChartLegendComponent>();
  changeVisibility =
    output<{ index: number; text: string; visibility: boolean }[]>();

  private eventListeners: (() => void)[] = [];

  ngOnInit() {
    this.ready.emit(this); // Emit the ready event when the component initializes
  }

  /**
   * Creates the legend dynamically based on the provided items and chart configuration.
   * @param items The list of legend items to be displayed.
   * @param chart The chart object to interact with for toggling dataset visibility.
   */
  createLegend(items: any[], chart: any) {
    const legendContainer = this.el.nativeElement.querySelector(
      `#${this.containerID()}`,
    ) as HTMLElement;

    // Clear existing legend content
    legendContainer.innerHTML = '';

    // Create container div
    const divContainer = this.renderer.createElement('div');
    this.renderer.addClass(divContainer, 'divContainer');
    this.renderer.appendChild(legendContainer, divContainer);

    // Create legend list
    const ul = this.renderer.createElement('ul');
    this.renderer.appendChild(divContainer, ul);

    const label = this.label();

    // Add label if provided
    if (label) {
      const titleLi = this.renderer.createElement('span');
      this.renderer.setStyle(titleLi, 'listStyle', 'none');
      this.renderer.setStyle(titleLi, 'marginLeft', '0px');
      this.renderer.setStyle(titleLi, 'marginRight', '0px');
      const titleText = this.renderer.createText(label);
      this.renderer.appendChild(titleLi, titleText);
      this.renderer.appendChild(divContainer, titleLi);
    }

    // Populate legend items
    items.forEach((item) => {
      const li = this.renderer.createElement('li');

      // Add click event listener for toggling visibility
      const listener = this.renderer.listen(li, 'click', () => {
        if (['pie', 'doughnut'].includes(chart.config.type)) {
          chart.toggleDataVisibility(item.index);
        } else {
          chart.setDatasetVisibility(
            item.datasetIndex,
            !chart.isDatasetVisible(item.datasetIndex),
          );

          this.changeVisibility.emit(
            items.map((itemVisibility) => ({
              index: itemVisibility.datasetIndex,
              text: itemVisibility.text,
              visibility: chart.isDatasetVisible(itemVisibility.datasetIndex),
            })),
          );
        }
        chart.update();
      });
      this.eventListeners.push(listener);

      // Create legend circle with styles
      const styledSpan = this.renderer.createElement('span');
      this.renderer.addClass(styledSpan, 'legend-circle');

      const backgroundColor = modifyRgbaTransparency(item.fillStyle, 0.2);
      const borderColor = modifyRgbaTransparency(item.strokeStyle, 0.5);
      this.renderer.setStyle(styledSpan, 'background', backgroundColor);
      this.renderer.setStyle(styledSpan, 'borderColor', borderColor);

      if (!chart.isDatasetVisible(item.datasetIndex)) {
        this.renderer.setStyle(styledSpan, 'filter', 'grayscale(100%)');
        this.renderer.setStyle(styledSpan, 'color', 'gray');
      } else {
        const textColor = darkenColor(item.strokeStyle, 0.3);
        this.renderer.setStyle(styledSpan, 'color', textColor);
      }

      // Add text to legend circle
      const text = this.renderer.createText(item.text);
      this.renderer.appendChild(styledSpan, text);
      this.renderer.appendChild(li, styledSpan);
      this.renderer.appendChild(ul, li);
      this.renderer.appendChild(divContainer, ul);
    });
  }

  ngOnDestroy() {
    // Remove event listeners to prevent memory leaks
    this.eventListeners.forEach((listener) => listener());
  }
}

/**
 * Adjusts the transparency of a color (RGBA or HEX).
 * @param color The original color in RGBA or HEX format.
 * @param transparency The desired transparency (0 to 1).
 * @returns The modified color with updated transparency.
 */
function modifyRgbaTransparency(color: string, transparency: number): string {
  if (transparency < 0 || transparency > 1) {
    throw new Error('Transparency must be between 0 and 1');
  }

  if (color.startsWith('#')) {
    // Convert HEX to RGBA
    const rgba = hexToRgba(color, transparency);
    if (!rgba) throw new Error('Invalid HEX color format');
    return rgba;
  }

  const rgbaRegex = /rgba?\((\d+), ?(\d+), ?(\d+)(?:, ?([\d.]+))?\)/i;
  const matches = color.match(rgbaRegex);

  if (!matches) {
    throw new Error('Invalid RGBA color format');
  }

  const [_, r, g, b] = matches;
  return `rgba(${r}, ${g}, ${b}, ${transparency})`;
}

/**
 * Darkens a color (HEX, RGB, or RGBA) by a percentage.
 * @param color The original color.
 * @param amount The percentage to darken (0 to 1).
 * @returns The darkened color.
 */
function darkenColor(color: string, amount: number): string {
  const hexToRgb = (hex: string) => {
    const normalizedHex = hex.replace('#', '');
    const bigint = parseInt(normalizedHex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
  };

  let r: number, g: number, b: number;
  let a = 1;

  if (color.startsWith('#')) {
    ({ r, g, b } = hexToRgb(color));
  } else {
    const rgbaRegex = /rgba?\((\d+), ?(\d+), ?(\d+)(?:, ?([\d.]+))?\)/i;
    const matches = color.match(rgbaRegex);
    if (!matches) throw new Error('Invalid color format');
    [r, g, b] = matches.slice(1, 4).map(Number);
    a = matches[4] ? parseFloat(matches[4]) : 1;
  }

  r = Math.max(0, Math.round(r - r * amount));
  g = Math.max(0, Math.round(g - g * amount));
  b = Math.max(0, Math.round(b - b * amount));

  return a < 1 ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
}

/**
 * Converts HEX to RGBA format.
 * @param hex The HEX color.
 * @param alpha The alpha value (0 to 1).
 * @returns The RGBA color.
 */
function hexToRgba(hex: string, alpha: number): string | null {
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (!hexRegex.test(hex)) return null;

  const normalizedHex =
    hex.length === 4 ? `#${[...hex.slice(1)].map((x) => x + x).join('')}` : hex;

  const bigint = parseInt(normalizedHex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
